import React, { useMemo, useRef, useState } from "react";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Popper from "@mui/material/Popper";
import Select, { SelectProps } from "@mui/material/Select/";
import styled from "styled-components";

import { Picker, PickerItem } from "@formant/ui/component/picker";

const StyledFormControl = styled(FormControl)`
    width: 100%;
`;

const StyledPopper = styled(Popper)`
    z-index: 1300;
`;

const StyledInputLabel = styled(InputLabel)`
    margin-left: -0.938rem;
`;

const StyledFormHelperText = styled(FormHelperText)`
    margin-left: 0.2rem;
`;

const StyledSelect = styled(Select)`
    color: var(--primary-module, #fff);
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.6px;
`;

const StyledPillSelect = styled(Select)`
    border-radius: 100px;
    background: var(--primary-steel-02, #3b4668);
    color: var(--primary-silver, #bac4e2);
    font-variant-numeric: lining-nums tabular-nums;
    font-feature-settings:
        "clig" off,
        "liga" off;
    font-family: Moderat;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.6px;
    padding: 1px 6px 1px 10px;
    justify-content: center;
    align-items: center;
    gap: 5px;

    &::before {
        border-bottom: none !important;
    }
`;

interface ICommonSelectPickerProps<T> {
    options: T[];
    selectedOption: T | undefined;
    onSetSelected: (option: T) => void;
    optionLabelKey: keyof T;
    optionIdKey: keyof T;
    selectProps?: SelectProps;
    helperTextProps?: { error: boolean; label: string };
    isPill?: boolean;
}
/**
 *
 * @param options - Array of options used on the picker component
 * @param selectedOption - Default selected option value
 * @param onSetSelected - Action to be executed once the option has been selected
 * @param optionLabelKey - Key used from the object to be  displayed on the label
 * @param optionIdKey - Key used from the object to be  used as the key of the selct
 * @param selectProps - Props that can be applied to MUI Select
 * @param helperTextProps - Props that can be applied to show helper text
 * @param isPill - Optional param to show the picker as a pill
 * 
 * 
 * usage example 
 * 
 *   
    <CommonSelectPicker<ITeam>
    onSetSelected={setSelectedTeam}
    optionIdKey="id"
    optionLabelKey="name"
    options={teams || []}
    selectedOption={selectedTeam}
    />
 * 
 * 
 */
export default function CommonSelectPicker<T>({
    selectedOption,
    options,
    onSetSelected,
    optionLabelKey,
    optionIdKey,
    selectProps,
    helperTextProps,
    isPill = false,
}: ICommonSelectPickerProps<T>) {
    const listOptions: PickerItem<never, never>[] = useMemo(() => {
        const optionValues = options || [];

        return optionValues.map(optionValue => {
            return {
                label: optionValue[optionLabelKey] as string,
                onClick: () => {
                    onSetSelected(optionValue);
                },
            };
        });
    }, [options, optionLabelKey]);
    const items = [...listOptions];

    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);

    const handleClick = () => {
        setOpen(val => !val);
    };

    const id = open
        ? `select-with-picker-${selectedOption?.[optionIdKey]}-popper`
        : undefined;

    return (
        <StyledFormControl>
            {!selectedOption ? (
                <StyledInputLabel shrink={false}>
                    {selectProps?.placeholder}
                </StyledInputLabel>
            ) : null}
            {isPill ? (
                <StyledPillSelect
                    ref={anchorRef}
                    aria-describedby={id}
                    onClick={handleClick}
                    onKeyDownCapture={ev => {
                        if (ev.key === "Enter") {
                            handleClick();
                            ev.preventDefault();
                        }
                    }}
                    id={`select-with-picker-${selectedOption?.[optionIdKey]}`}
                    value={selectedOption?.[optionIdKey] || ""}
                    renderValue={() => (
                        <>{String(selectedOption?.[optionLabelKey] ?? "")}</>
                    )}
                    open={false}
                    variant="standard"
                    {...selectProps}
                />
            ) : (
                <StyledSelect
                    ref={anchorRef}
                    aria-describedby={id}
                    onClick={handleClick}
                    onKeyDownCapture={ev => {
                        if (ev.key === "Enter") {
                            handleClick();
                            ev.preventDefault();
                        }
                    }}
                    id={`select-with-picker-${selectedOption?.[optionIdKey]}`}
                    value={selectedOption?.[optionIdKey] || ""}
                    renderValue={() => (
                        <>{String(selectedOption?.[optionLabelKey] ?? "")}</>
                    )}
                    open={false}
                    variant="standard"
                    {...selectProps}
                />
            )}
            <StyledFormHelperText error={helperTextProps?.error}>
                {helperTextProps?.label}
            </StyledFormHelperText>

            <StyledPopper id={id} open={open} anchorEl={anchorRef.current}>
                <ClickAwayListener onClickAway={handleClick}>
                    <Picker
                        onSelectItem={handleClick}
                        displayType="dropdown"
                        searchPlaceholder="Search teams"
                        noAutoSelectFirst={true}
                        items={items}
                        style={{
                            minWidth: "100%",
                            maxWidth: "100%",
                            minHeight: "100%",
                            maxHeight: "35vh",
                        }}
                    />
                </ClickAwayListener>
            </StyledPopper>
        </StyledFormControl>
    );
}
